import { memo, useEffect, useState, useRef } from 'react';

import {
	DropDownList,
	DropDownListLi,
	DropDownListGroup
} from './drop-down-list.styles';
import DropDownItemComponent from '../drop-down-item/drop-down-item.component';
import { SelectOptionGroup, SelectOption } from '../../types/config';

import { IconTravelMapMarkerComponent } from '@vaa-component-lib/component.atom.icons';
import {
	TypographyComponent,
	TypographySize,
	TypographyVariant,
	TypographyWeight
} from '@vaa-component-lib/component.atom.typography';

import sanitiseString from '../../lib/utils/sanitise-string';

interface DropDownListGroupComponentProps {
	items: SelectOptionGroup[];
	onSelect: (option: SelectOption) => void;
	isOpen: boolean;
	clearSelection?: boolean;
}

const DropDownListGroupComponent = ({
	items,
	onSelect,
	isOpen = false,
	clearSelection
}: DropDownListGroupComponentProps) => {
	const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
	const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

	const selected = items
		?.flatMap((group) => group?.options)
		.find((item) => item?.selected === true);
	const [selectedValue, setSelectedValue] = useState<string | number>(
		selected?.value || ''
	);

	const handleSelection = (option: SelectOption) => {
		onSelect(option);
		setSelectedValue(option.value);
	};

	const handleKeyDown = (event: KeyboardEvent) => {
		const { key } = event;
		if (focusedIndex === null) return;

		switch (key) {
			case 'ArrowDown':
				const nextIndex = (focusedIndex + 1) % items.length;
				setFocusedIndex(nextIndex);
				optionRefs.current[nextIndex]?.focus();
				break;

			case 'ArrowUp':
				const prevIndex = (focusedIndex - 1 + items.length) % items.length;
				setFocusedIndex(prevIndex);
				optionRefs.current[prevIndex]?.focus();
				break;

			default:
				break;
		}
	};

	useEffect(() => {
		if (isOpen) {
			setFocusedIndex(-1);
		} else {
			setFocusedIndex(null);
		}
	}, [isOpen]);

	useEffect(() => {
		if (isOpen) {
			window.addEventListener('keydown', handleKeyDown);
		}

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen, focusedIndex]);

	useEffect(() => {
		if (clearSelection) {
			setSelectedValue('');
		}
	}, [clearSelection]);

	return (
		<DropDownList
			maxHeight={true}
			data-cy="drop-down-list-component"
			role="listbox">
			{items.map((item) => (
				<DropDownListGroup key={`dropdown-group-${sanitiseString(item.group)}`}>
					<TypographyComponent
						element="h6"
						variant={TypographyVariant.Heading}
						size={TypographySize.Xxs}
						weight={TypographyWeight.Medium}>
						{item?.group}
					</TypographyComponent>
					{item?.options.map((option, index) => (
						<DropDownListLi key={`dropdown-${sanitiseString(option.label)}`}>
							<DropDownItemComponent
								option={{
									label: option.label,
									value: option.value,
									selectedLabel: option.selectedLabel,
									selected: selectedValue === option.value
								}}
								onSelect={(option: SelectOption) => handleSelection(option)}
								isActive={isOpen}
								ref={(el) => (optionRefs.current[index] = el)}
								itemIcon={<IconTravelMapMarkerComponent />}
							/>
						</DropDownListLi>
					))}
				</DropDownListGroup>
			))}
		</DropDownList>
	);
};

export default memo(DropDownListGroupComponent);
